import React from "react";
import { Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import cookies from "js-cookie";
import "./auth.css";

/**
 * Coquille commune aux écrans de connexion et de création de compte
 * (design "Connexion et créneaux", écrans 01 et 02) : panneau indigo à
 * gauche, formulaire à droite. Les deux écrans ne diffèrent que par
 * l'argumentaire de gauche et le contenu du formulaire.
 */

const LANGUAGES = [
  { code: "en", label: "English", flag: "/assets/imgs/UK-flag.png" },
  { code: "fn", label: "Français", flag: "/assets/imgs/FR-flag.png" },
  { code: "du", label: "Nederlands", flag: "/assets/imgs/NL-flag.png" },
];

const currentLanguage = () =>
  LANGUAGES.find((language) => language.code === cookies.get("i18next")) ||
  LANGUAGES[0];

/** Sprite SVG : les icônes du design, déclarées une fois par écran. */
export const AuthIcons = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <symbol id="i-mail" viewBox="0 0 512 512">
        <rect
          x="48"
          y="96"
          width="416"
          height="320"
          rx="40"
          ry="40"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
        <polyline
          points="112 160 256 272 400 160"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
      </symbol>
      <symbol id="i-lock" viewBox="0 0 512 512">
        <path
          d="M336,208V113a80,80,0,0,0-160,0v95"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
        <rect
          x="96"
          y="208"
          width="320"
          height="272"
          rx="48"
          ry="48"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
      </symbol>
      <symbol id="i-eye" viewBox="0 0 512 512">
        <path
          d="M255.66,112c-77.94,0-157.89,45.11-220.83,135.33a16,16,0,0,0-.27,17.77C82.92,340.8,161.8,400,255.66,400,348.5,400,429,340.62,477.45,264.75a16.14,16.14,0,0,0,0-17.47C428.89,172.28,347.8,112,255.66,112Z"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
        <circle
          cx="256"
          cy="256"
          r="80"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeMiterlimit: 10,
            strokeWidth: "32px",
          }}
        />
      </symbol>
      <symbol id="i-chevron-down" viewBox="0 0 512 512">
        <polyline
          points="112 184 256 328 400 184"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "48px",
          }}
        />
      </symbol>
      <symbol id="i-check" viewBox="0 0 512 512">
        <polyline
          points="416 128 192 384 96 288"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "44px",
          }}
        />
      </symbol>
      <symbol id="i-time" viewBox="0 0 512 512">
        <path
          d="M256,64C150,64,64,150,64,256s86,192,192,192,192-86,192-192S362,64,256,64Z"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeMiterlimit: 10,
            strokeWidth: "32px",
          }}
        />
        <polyline
          points="256 128 256 272 352 272"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
      </symbol>
      <symbol id="i-location" viewBox="0 0 512 512">
        <path
          d="M256,48c-79.5,0-144,61.39-144,137,0,87,96,224.87,131.25,272.49a15.77,15.77,0,0,0,25.5,0C304,409.89,400,272.07,400,185,400,109.39,335.5,48,256,48Z"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
        <circle
          cx="256"
          cy="192"
          r="48"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
      </symbol>
      <symbol id="i-water" viewBox="0 0 512 512">
        <path
          d="M400,320c0,88.37-55.63,144-144,144S112,408.37,112,320c0-94.83,103.23-222.85,134.89-259.88a12,12,0,0,1,18.23,0C296.77,97.15,400,225.17,400,320Z"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeMiterlimit: 10,
            strokeWidth: "32px",
          }}
        />
        <path
          d="M344,328a72,72,0,0,1-72,72"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
      </symbol>
      <symbol id="i-apple" viewBox="0 0 512 512">
        <path
          d="M349.13,136.86c-40.32,0-57.36,19.24-85.44,19.24C234.9,156.1,212.94,137,178,137c-34.2,0-70.67,20.88-93.83,56.45-32.52,50.16-27,144.63,25.67,225.11,18.84,28.81,44,61.12,77,61.47h.6c28.68,0,37.2-18.78,76.67-19h.6c38.88,0,46.68,18.89,75.24,18.89h.6c33-.35,59.51-36.15,78.35-64.85,13.56-20.64,18.6-31,29-54.35-76.19-28.92-88.43-136.93-13.08-178.34-23-28.8-55.32-45.48-85.79-45.48Z"
          style={{ fill: "currentColor" }}
        />
        <path
          d="M340.25,32c-24,1.63-52,16.91-68.4,36.86-14.88,18.08-27.12,44.9-22.32,70.91h1.92c25.56,0,51.72-15.39,67-35.11C333.17,85.89,344.33,59.29,340.25,32Z"
          style={{ fill: "currentColor" }}
        />
      </symbol>
      <symbol id="i-store" viewBox="0 0 512 512">
        <path
          d="M448 448V240M64 240v208M382.47 48H129.53c-21.79 0-41.47 12-49.93 30.46L36.3 173c-14.58 31.81 9.63 67.85 47.19 69h2c31.4 0 56.85-25.18 56.85-52.23 0 27 25.46 52.23 56.86 52.23s56.8-23.38 56.8-52.23c0 27 25.45 52.23 56.85 52.23s56.86-23.38 56.86-52.23c0 28.85 25.45 52.23 56.85 52.23h1.95c37.56-1.17 61.77-37.21 47.19-69l-43.3-94.54C423.94 60 404.26 48 382.47 48zM32 464h448M136 288h80a24 24 0 0124 24v88h0-128 0v-88a24 24 0 0124-24zM288 464V312a24 24 0 0124-24h64a24 24 0 0124 24v152"
          style={{
            fill: "none",
            stroke: "currentColor",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            strokeWidth: "32px",
          }}
        />
      </symbol>
    </defs>
  </svg>
);

/** Icône du sprite, dimensionnée par la CSS du contexte. */
export const Icon = ({ name, ...rest }) => (
  <svg viewBox="0 0 512 512" aria-hidden="true" {...rest}>
    <use href={`#${name}`} />
  </svg>
);

/**
 * Pastille de langue. Le choix est persisté dans le cookie i18next, lu au
 * démarrage par i18n.js — d'où le rechargement, comme dans l'ancien écran.
 */
export class LanguagePill extends React.Component {
  constructor(props) {
    super(props);
    this.state = { open: false };
    this.container = React.createRef();
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleOutsideClick);
  }

  handleOutsideClick = (event) => {
    if (
      this.state.open &&
      this.container.current &&
      !this.container.current.contains(event.target)
    ) {
      this.setState({ open: false });
    }
  };

  select = (language) => {
    cookies.set("i18next", language.code);
    window.location.reload();
  };

  render() {
    const { open } = this.state;
    const active = currentLanguage();

    return (
      <div
        className={`wosh-auth__lang${open ? " wosh-auth__lang--open" : ""}`}
        ref={this.container}
      >
        <button
          type="button"
          className="wosh-auth__lang-toggle"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => this.setState((prev) => ({ open: !prev.open }))}
        >
          {active.label}
          <Icon name="i-chevron-down" />
        </button>
        {open && (
          <div className="wosh-auth__lang-menu" role="listbox">
            {LANGUAGES.map((language) => (
              <button
                type="button"
                role="option"
                aria-selected={language.code === active.code}
                key={language.code}
                className="wosh-auth__lang-option"
                onClick={() => this.select(language)}
              >
                {language.label}
                <img src={language.flag} alt="" />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
}

/**
 * Apple ne peut être proposé que si le client ID est configuré : sans lui, le
 * bouton ouvrirait une popup vouée à l'échec. Voir aussi Config.appleLogin,
 * qui lit REACT_APP_APPLE_LOGIN pour l'échange de jeton côté back.
 */
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID;
const APPLE_REDIRECT_URI =
  process.env.REACT_APP_APPLE_REDIRECT_URI ||
  `${window.location.origin}/sign-in`;

/** Le widget Google veut la locale en BCP 47, pas le code du cookie. */
const googleLocale = () =>
  cookies.get("i18next") === "fn"
    ? "fr"
    : cookies.get("i18next") === "du"
    ? "nl"
    : "en";

/** Largeurs extrêmes acceptées par le bouton Google. */
const GOOGLE_MIN_WIDTH = 200;
const GOOGLE_MAX_WIDTH = 400;

/**
 * Connexions sociales. `split` place Google et Apple côte à côte (écran
 * d'inscription) ; sans Apple configuré, Google reste seul et pleine largeur.
 */
export const SocialSignIn = ({ t, onGoogle, onApple, split = false }) => {
  const sideBySide = split && Boolean(APPLE_CLIENT_ID);
  const googleBox = React.useRef(null);
  const [googleWidth, setGoogleWidth] = React.useState(GOOGLE_MAX_WIDTH);

  /*
   * Le bouton de Google reste visible et tel quel : son iframe (bouton
   * personnalisé « Continuer en tant que… ») ignore les clics quand elle est
   * masquée ou déformée — c'est sa protection anti-clickjacking. On lui donne
   * seulement la largeur de la colonne, dans la plage que Google accepte.
   */
  React.useLayoutEffect(() => {
    const box = googleBox.current;

    if (!box) {
      return undefined;
    }

    const measure = () => {
      const width = Math.round(box.getBoundingClientRect().width);

      if (width) {
        setGoogleWidth(
          Math.max(GOOGLE_MIN_WIDTH, Math.min(GOOGLE_MAX_WIDTH, width))
        );
      }
    };

    measure();
    window.addEventListener("resize", measure);

    return () => window.removeEventListener("resize", measure);
  }, []);

  return (
    <div
      className={`wosh-auth__socials${
        sideBySide ? " wosh-auth__socials--split" : ""
      }`}
    >
      <div className="wosh-auth__google" ref={googleBox}>
        <GoogleLogin
          onSuccess={onGoogle}
          shape="pill"
          size="large"
          theme="outline"
          width={String(googleWidth)}
          text="continue_with"
          locale={googleLocale()}
          logo_alignment="center"
        />
      </div>

      {APPLE_CLIENT_ID && (
        <AppleLogin
          clientId={APPLE_CLIENT_ID}
          redirectURI={APPLE_REDIRECT_URI}
          usePopup
          scope="email name"
          responseType="id_token"
          callback={(response) => onApple(response.authorization)}
          render={(renderProps) => (
            <button
              type="button"
              className="wosh-auth__social"
              onClick={renderProps.onClick}
            >
              <Icon name="i-apple" />
              {sideBySide ? "Apple" : t("Continue with Apple")}
            </button>
          )}
        />
      )}
    </div>
  );
};

const LegalLinks = ({ t }) => (
  <nav className="wosh-auth__legal">
    <a href="https://www.wosh.be/" target="_blank" rel="noreferrer">
      {t("How it works")}
    </a>
    <a href="https://www.wosh.be/legal/privacy-policy" target="_blank" rel="noreferrer">
      {t("Privacy Policy")}
    </a>
    <a href="https://www.wosh.be/legal/terms-conditions" target="_blank" rel="noreferrer">
      {t("Terms & Conditions")}
    </a>
  </nav>
);

/**
 * Cadre des écrans d'authentification : panneau lavande illustré à gauche,
 * formulaire à droite.
 *
 * @param {"signin"|"signup"} variant  Illustration du panneau :
 *   signin → téléphone débordant de linge, pastille `chip`, liens légaux ;
 *   signup → vêtements + scooter, carte des étapes `steps` et liens légaux.
 * @param {string} eyebrow   Surtitre (« Laundry & dry-cleaning »).
 * @param {string} title     Accroche.
 * @param {string} lede      Sous-titre.
 * @param {{icon, label}} chip          Pastille sous le sous-titre (signin).
 * @param {string[]} steps              Étapes de l'inscription (signup).
 * @param {number} activeStep           Étape en cours, à partir de 1.
 * @param {boolean} wide     Colonne de formulaire élargie (inscription).
 */
const AuthLayout = ({
  t,
  variant = "signin",
  eyebrow,
  title,
  lede,
  chip,
  steps = [],
  activeStep = 1,
  wide = false,
  children,
}) => (
  <div className={`wosh-auth${wide ? " wosh-auth--compact" : ""}`}>
    <AuthIcons />
    <div className="wosh-auth__shell">
      <aside className={`wosh-auth__aside wosh-auth__aside--${variant}`}>
        {variant === "signup" && (
          <>
            <span className="wosh-auth__disc wosh-auth__disc--lav" aria-hidden="true" />
            <span className="wosh-auth__disc wosh-auth__disc--mint" aria-hidden="true" />
            <img className="wosh-auth__art-clothes" src="/assets/imgs/illustration-clothes.png" alt="" />
            <img className="wosh-auth__art-scooter" src="/assets/imgs/illustration-scooter.png" alt="" />
          </>
        )}

        <Link to="/sign-in" className="wosh-auth__wordmark">
          WOSH
        </Link>

        <div className="wosh-auth__pitch">
          {eyebrow && <div className="wosh-auth__eyebrow">{eyebrow}</div>}
          <h2>{title}</h2>
          <p>{lede}</p>
          {chip && (
            <span className="wosh-auth__chip">
              <span className="wosh-auth__chip-icon">
                <Icon name={chip.icon} />
              </span>
              {chip.label}
            </span>
          )}
        </div>

        {variant === "signin" && (
          <div className="wosh-auth__art-phone">
            <img src="/assets/imgs/illustration-phone.png" alt="" />
            <LegalLinks t={t} />
          </div>
        )}

        {variant === "signup" && (
          <div className="wosh-auth__foot">
            {steps.length > 0 && (
              <ol className="wosh-auth__steps">
                {steps.map((label, index) => (
                  <li
                    key={label}
                    className={`wosh-auth__step${index + 1 === activeStep ? " wosh-auth__step--on" : ""}`}
                    aria-current={index + 1 === activeStep ? "step" : undefined}
                  >
                    <span className="wosh-auth__step-num">{index + 1}</span>
                    {label}
                  </li>
                ))}
              </ol>
            )}
            <LegalLinks t={t} />
          </div>
        )}
      </aside>

      <main className="wosh-auth__panel">
        <div className="wosh-auth__topbar">
          <LanguagePill />
        </div>
        <div className={`wosh-auth__body${wide ? " wosh-auth__body--wide" : ""}`}>
          {children}
        </div>
      </main>
    </div>
  </div>
);

export default AuthLayout;
